import { EntityInfo } from '@record-it-npm/synchro-common';
import { DisposableContainer } from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import { EntityFilterContainer } from '../EntityFilter/EntityFilterContainer';
import { EntityManagerBaseEntity } from '../EntityManagerBaseEntity';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';
export class VisibleEntities<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityName extends TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> {
  private readonly filterContainer: EntityFilterContainer<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>;
  private readonly entityInfo: EntityInfo<TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'], TEntityName, TEntityTypesByEntityName[TEntityName]['entity']>;
  private readonly onEntityVisibilityChanged: OnEntityVisibilityChanged<TSynchronizationEnvironmentTypes, TEntityName, TEntityTypesByEntityName>;
  private readonly disposableContainer = new DisposableContainer();
  private visibleEntitiesById = new Map<string, TEntityTypesByEntityName[TEntityName]['entity']>();
  private allEntitiesById = new Map<string, TEntityTypesByEntityName[TEntityName]['entity']>();
  constructor({
    filterContainer,
    entityInfo,
    onEntityVisibilityChanged
  }: {
    filterContainer: EntityFilterContainer<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>;
    entityInfo: EntityInfo<TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'], TEntityName, TEntityTypesByEntityName[TEntityName]['entity']>;
    onEntityVisibilityChanged: OnEntityVisibilityChanged<TSynchronizationEnvironmentTypes, TEntityName, TEntityTypesByEntityName>;
  }) {
    this.filterContainer = filterContainer;
    this.entityInfo = entityInfo;
    this.onEntityVisibilityChanged = onEntityVisibilityChanged;
  }
  public init({
    allEntitiesById
  }: {
    allEntitiesById: Map<string, TEntityTypesByEntityName[TEntityName]['entity']>;
  }): void {
    this.allEntitiesById = allEntitiesById;
    this.visibleEntitiesById = this.createVisibleEntitiesById({
      allEntitiesById
    });
    this.disposableContainer.add(this.filterContainer.registerUpdateListener({
      entityName: this.entityInfo.entityName,
      callback: () => {
        this.updateVisibleEntities({
          entitiesToIgnoreForNotifications: []
        });
      }
    }));
  }
  public destroy(): void {
    this.disposableContainer.disposeAll();
  }
  public getByIdOrOriginalId(anyId: EntityManagerBaseEntity['id']): TEntityTypesByEntityName[TEntityName]['entity'] | null {
    const entityById = this.getById(anyId);
    if (entityById) return entityById;
    return this.getByOriginalId(anyId);
  }
  public getById(id: string): TEntityTypesByEntityName[TEntityName]['entity'] | null {
    return this.visibleEntitiesById.get(id) ?? null;
  }

  /**
   * @performance ideally we would have a map from the originalId to the entity itself
   */
  public getByOriginalId(originalId: EntityManagerBaseEntity['id']): TEntityTypesByEntityName[TEntityName]['entity'] | null {
    return this.getAll().find(e => e.originalIds.includes(originalId)) ?? null;
  }
  public getAll(): Array<TEntityTypesByEntityName[TEntityName]['entity']> {
    return Array.from(this.visibleEntitiesById.values());
  }
  public addEntity(entity: TEntityTypesByEntityName[TEntityName]['entity']): void {
    this.allEntitiesById.set(entity.id, entity);
    if (this.filterContainer.isEntityVisible({
      entity,
      entityName: this.entityInfo.entityName
    })) {
      this.visibleEntitiesById.set(entity.id, entity);
    }
  }
  public removeEntity(entity: TEntityTypesByEntityName[TEntityName]['entity']): void {
    this.allEntitiesById.delete(entity.id);
    this.visibleEntitiesById.delete(entity.id);
  }
  public updateEntity(entity: TEntityTypesByEntityName[TEntityName]['entity']): void {
    const entityWasIncluded = [entity.id, ...entity.originalIds].some(id => this.allEntitiesById.get(id));

    // new entities can only be added with add entity
    if (!entityWasIncluded) {
      return;
    }
    this.allEntitiesById.set(entity.id, entity);
    const entityWasVisible = [entity.id, ...entity.originalIds].some(id => this.visibleEntitiesById.get(id));
    const entityIsVisible = this.filterContainer.isEntityVisible({
      entity,
      entityName: this.entityInfo.entityName
    });
    if (entityIsVisible) {
      this.visibleEntitiesById.set(entity.id, entity);
    } else {
      this.visibleEntitiesById.delete(entity.id);
    }
    for (const originalId of entity.originalIds) {
      if (originalId === entity.id) {
        continue;
      }
      this.allEntitiesById.delete(originalId);
      this.visibleEntitiesById.delete(originalId);
    }
    if (entityIsVisible !== entityWasVisible) {
      this.onEntityVisibilityChanged({
        entity
      });
    }
  }
  private updateVisibleEntities({
    entitiesToIgnoreForNotifications
  }: {
    entitiesToIgnoreForNotifications: Array<TEntityTypesByEntityName[TEntityName]['entity']>;
  }): void {
    const previousVisibleEntitiesById = this.visibleEntitiesById;
    this.visibleEntitiesById = this.createVisibleEntitiesById({
      allEntitiesById: this.allEntitiesById
    });
    this.notifyVisibleEntityChanges({
      previousVisibleEntitiesById,
      visibleEntitiesById: this.visibleEntitiesById,
      entitiesToIgnoreForNotifications
    });
  }
  private createVisibleEntitiesById({
    allEntitiesById
  }: {
    allEntitiesById: Map<string, TEntityTypesByEntityName[TEntityName]['entity']>;
  }): Map<string, TEntityTypesByEntityName[TEntityName]['entity']> {
    const visibleEntities = Array.from(allEntitiesById.values()).filter(entity => {
      return this.filterContainer.isEntityVisible({
        entity,
        entityName: this.entityInfo.entityName
      });
    });
    return new Map(visibleEntities.map(entity => [entity.id, entity]));
  }
  private notifyVisibleEntityChanges({
    previousVisibleEntitiesById,
    visibleEntitiesById,
    entitiesToIgnoreForNotifications
  }: {
    previousVisibleEntitiesById: Map<string, TEntityTypesByEntityName[TEntityName]['entity']>;
    visibleEntitiesById: Map<string, TEntityTypesByEntityName[TEntityName]['entity']>;
    entitiesToIgnoreForNotifications: Array<TEntityTypesByEntityName[TEntityName]['entity']>;
  }): void {
    const entitiesWithVisibilityChange = [...this.getEntitiesNotIncludedInOtherMapOneWay({
      entitiesById1: visibleEntitiesById,
      entitiesById2: previousVisibleEntitiesById
    }), ...this.getEntitiesNotIncludedInOtherMapOneWay({
      entitiesById1: previousVisibleEntitiesById,
      entitiesById2: visibleEntitiesById
    })];
    const idsToIgnore = new Set(entitiesToIgnoreForNotifications.map(e => e.id));
    for (const entity of entitiesWithVisibilityChange) {
      if (idsToIgnore.has(entity.id)) {
        continue;
      }
      this.onEntityVisibilityChanged({
        entity
      });
    }
  }

  /**
   * returns all entities of entitiesById1 which are not included in entitiesById2
   */
  private getEntitiesNotIncludedInOtherMapOneWay({
    entitiesById1,
    entitiesById2
  }: {
    entitiesById1: Map<string, TEntityTypesByEntityName[TEntityName]['entity']>;
    entitiesById2: Map<string, TEntityTypesByEntityName[TEntityName]['entity']>;
  }): Array<TEntityTypesByEntityName[TEntityName]['entity']> {
    const entities: Array<TEntityTypesByEntityName[TEntityName]['entity']> = [];
    for (const entity of entitiesById1.values()) {
      if (!entitiesById2.has(entity.id)) {
        entities.push(entity);
      }
    }
    return entities;
  }
}
export type OnEntityVisibilityChanged<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityName extends TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> = (options: {
  entity: TEntityTypesByEntityName[TEntityName]['entity'];
}) => void;